import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { confirmWaitlist } from "../lib/api";

/**
 * WaitlistConfirm — double-opt-in landing page. Reads ?token, flips the
 * waitlist row to verified via the API, and shows the member number.
 */
export default function WaitlistConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [memberNo, setMemberNo] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [err, setErr] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus("error");
      setErr("This link is missing its confirmation token.");
      return;
    }
    confirmWaitlist(token)
      .then((d) => {
        setMemberNo(d?.founding_number ?? null);
        setFirstName(d?.first_name || "");
        setStatus("ok");
      })
      .catch((e) => {
        setStatus("error");
        setErr(e?.response?.data?.detail || "We couldn't confirm this link.");
      });
  }, [token]);

  return (
    <main
      data-testid="waitlist-confirm-page"
      className="min-h-screen bg-cream-50 flex items-center justify-center px-5 py-24"
    >
      <div className="w-full max-w-md rounded-2xl border border-surface-line bg-white p-8 text-center shadow-card">
        {status === "loading" && (
          <div data-testid="confirm-loading" className="py-6 text-ink-mute">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            <p className="mt-3 text-[14px]">Confirming your spot…</p>
          </div>
        )}

        {status === "ok" && (
          <div data-testid="confirm-success">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#0e6b34]" />
            <h1 className="mt-4 text-[24px] font-extrabold tracking-[-0.01em] text-ink">
              {memberNo ? `Confirmed — you're #${memberNo}` : "You're confirmed"}
            </h1>
            <p className="mt-2 text-[14.5px] text-ink-soft leading-relaxed">
              {firstName ? `Thanks, ${firstName}. ` : ""}
              You're a confirmed founding member. Pro will be free for 12 months
              when payments launch — we'll email you once, when it's ready.
            </p>
            <Link
              to="/check"
              data-testid="confirm-cta-check"
              className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-semibold transition-colors"
            >
              Run a free check <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {status === "error" && (
          <div data-testid="confirm-error">
            <XCircle className="w-10 h-10 mx-auto text-red-500" />
            <h1 className="mt-4 text-[22px] font-extrabold tracking-[-0.01em] text-ink">
              Confirmation failed
            </h1>
            <p className="mt-2 text-[14.5px] text-ink-soft leading-relaxed">{err}</p>
            <Link
              to="/#founding"
              data-testid="confirm-cta-rejoin"
              className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl border border-surface-line bg-white text-ink text-[14px] font-semibold hover:border-ink/30 transition-colors"
            >
              Re-join the waitlist
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
